<script setup>
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import LeadCaptureAPI from 'dashboard/api/leadCaptureEndpoints';

const props = defineProps({
  endpoint: { type: Object, default: null },
  inboxes: { type: Array, required: true },
});
const emit = defineEmits(['saved', 'close']);

const isEdit = computed(() => !!props.endpoint?.id);

const form = ref({
  name: '',
  endpoint_type: 'wa_redirect',
  inbox_id: null,
  message_template: '',
  send_proactive: false,
  active: true,
  rate_limit_per_minute: 15,
  capture_config: {
    gclid: true,
    fbclid: true,
    ctwa_clid: true,
    utms: true,
    private_note: false,
    private_note_intro: '',
    qualification_rules: [],
    qualified_label: '',
    disqualified_label: '',
    skip_proactive_if_unqualified: false,
    meta_page_id: '',
    meta_form_id: '',
    meta_fields: [],
  },
  origin_allowlist: [],
});
const allowlistInput = ref('');
const imageFile = ref(null);
const removeImage = ref(false);
const saving = ref(false);

watch(() => props.endpoint, ep => {
  if (!ep) return;
  form.value = {
    name: ep.name,
    endpoint_type: ep.endpoint_type,
    inbox_id: ep.inbox_id,
    message_template: ep.message_template || '',
    send_proactive: ep.send_proactive,
    active: ep.active,
    rate_limit_per_minute: ep.rate_limit_per_minute,
    capture_config: {
      gclid: true,
      fbclid: true,
      ctwa_clid: true,
      utms: true,
      private_note: false,
      private_note_intro: '',
      qualified_label: '',
      disqualified_label: '',
      skip_proactive_if_unqualified: false,
      meta_page_id: '',
      meta_form_id: '',
      ...ep.capture_config,
      qualification_rules: [...(ep.capture_config?.qualification_rules || [])],
      meta_fields: [...(ep.capture_config?.meta_fields || [])],
    },
    origin_allowlist: [...(ep.origin_allowlist || [])],
  };
  allowlistInput.value = (ep.origin_allowlist || []).join(', ');
}, { immediate: true });

const whatsappInboxes = computed(() => props.inboxes.filter(
  i => i.channel_type === 'Channel::Whatsapp' || i.channel_type === 'Channel::Api' || i.channel_type === 'Channel::WhatsappWeb'
));
const selectedInbox = computed(() => whatsappInboxes.value.find(i => i.id === form.value.inbox_id));
const inboxIsCloudApi = computed(() => {
  const ib = selectedInbox.value;
  return ib?.channel_type === 'Channel::Whatsapp' && ib?.provider === 'whatsapp_cloud';
});

const supportsProactive = computed(() => {
  const ib = selectedInbox.value;
  return ib && (ib.channel_type === 'Channel::Api' || ib.channel_type === 'Channel::WhatsappWeb');
});
watch(supportsProactive, val => { if (!val) form.value.send_proactive = false; });
watch(() => form.value.endpoint_type, val => { if (val === 'wa_redirect') form.value.send_proactive = false; });

async function save() {
  if (!form.value.name?.trim()) return useAlert('De um nome ao capturador');
  if (!form.value.inbox_id) return useAlert('Escolha uma inbox');
  saving.value = true;
  try {
    form.value.origin_allowlist = (allowlistInput.value || '').split(/[, ]+/).map(s => s.trim()).filter(Boolean);
    if (isEdit.value) {
      await LeadCaptureAPI.updateWithImage(props.endpoint.id, form.value, imageFile.value, removeImage.value);
    } else {
      await LeadCaptureAPI.createWithImage(form.value, imageFile.value);
    }
    useAlert(isEdit.value ? 'Capturador atualizado' : 'Capturador criado');
    emit('saved');
  } catch (e) {
    useAlert(e?.response?.data?.error || e?.response?.data?.message || 'Erro ao salvar');
  } finally {
    saving.value = false;
  }
}

function onFile(e) {
  imageFile.value = e.target.files?.[0] || null;
  if (imageFile.value) removeImage.value = false;
}

// SiteUp v309: importa os campos do formulario Meta Lead Ads pra montar a
// qualificacao por opcao sem digitar as chaves na mao.
const importing = ref(false);
const optionFields = computed(
  () => (form.value.capture_config.meta_fields || []).filter(f => (f.options || []).length)
);

async function importMetaFields() {
  const formId = (form.value.capture_config.meta_form_id || '').trim();
  if (!formId) return useAlert('Informe o ID do formulario Meta');
  importing.value = true;
  try {
    const { data } = await LeadCaptureAPI.getFormFields(formId);
    form.value.capture_config.meta_fields = data.fields || [];
    useAlert(`${(data.fields || []).length} campos importados${data.name ? ` de "${data.name}"` : ''}`);
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao importar campos do formulario');
  } finally {
    importing.value = false;
  }
}

// As opcoes marcadas como qualificadas viram UMA regra 'in' por campo, que o
// backend avalia com value_list. Separador = quebra de linha (NAO virgula):
// opcoes de formulario podem conter virgula (ex.: "Sim, mais de 1 ano").
function qualifyingOptions(fieldKey) {
  const rule = form.value.capture_config.qualification_rules.find(r => r.field === fieldKey && r.op === 'in');
  return rule ? (rule.value || '').split('\n').map(s => s.trim()).filter(Boolean) : [];
}
function isOptionQualifying(fieldKey, option) {
  return qualifyingOptions(fieldKey).includes(option);
}
function toggleOption(fieldKey, option) {
  const rules = form.value.capture_config.qualification_rules;
  let opts = qualifyingOptions(fieldKey);
  opts = opts.includes(option) ? opts.filter(o => o !== option) : [...opts, option];
  const idx = rules.findIndex(r => r.field === fieldKey && r.op === 'in');
  if (opts.length === 0) {
    if (idx >= 0) rules.splice(idx, 1);
  } else if (idx >= 0) {
    rules[idx].value = opts.join('\n');
  } else {
    rules.push({ field: fieldKey, op: 'in', value: opts.join('\n') });
  }
}

const messagePlaceholder = computed(() => form.value.endpoint_type === 'wa_redirect'
  ? 'Olá! Encontrei voces no Google e quero saber mais sobre...'
  : 'Olá {nome}! Recebemos seu pedido. Como podemos ajudar?');
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="$emit('close')">
    <div class="bg-n-background rounded-xl w-[90%] max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
      <header class="flex justify-between items-center px-6 py-4 border-b border-n-slate-4">
        <h3 class="text-base font-semibold text-n-slate-12">{{ isEdit ? 'Editar capturador' : 'Novo capturador' }}</h3>
        <button class="text-n-slate-11 hover:text-n-slate-12 text-xl" @click="$emit('close')">&times;</button>
      </header>

      <div class="px-6 py-5 overflow-y-auto flex-1">
        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">Nome interno</span>
          <input v-model="form.name" type="text" placeholder="Ex: Landing Dedetizacao"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-n-brand" />
        </label>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">Tipo</span>
          <select v-model="form.endpoint_type" :disabled="isEdit"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm">
            <option value="wa_redirect">Botao WhatsApp (URL pra link/anuncio)</option>
            <option value="webhook">Webhook (POST de form-builder)</option>
          </select>
          <small class="block mt-1 text-xs text-n-slate-11">
            <b>Botao WhatsApp</b>: gera URL pra colar como href ou Final URL de anuncio.<br>
            <b>Webhook</b>: gera URL pra colar em Tally/Typeform/n8n.
          </small>
        </label>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">Inbox WhatsApp</span>
          <select v-model="form.inbox_id"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm">
            <option :value="null">Escolha uma inbox</option>
            <option v-for="i in whatsappInboxes" :key="i.id" :value="i.id">
              {{ i.name }} ({{ i.channel_type === 'Channel::Whatsapp' ? 'Cloud API' : 'WAHA/Custom' }})
            </option>
          </select>
        </label>

        <!-- Mensagem template visivel APENAS pro tipo Botao WhatsApp.
             No tipo Webhook, a mensagem so faz sentido se WAHA + proativa,
             entao foi movida pra dentro do bloco proativo abaixo. -->
        <label v-if="form.endpoint_type === 'wa_redirect'" class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">Mensagem pre-preenchida no WhatsApp</span>
          <textarea v-model="form.message_template" rows="3" :placeholder="messagePlaceholder"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm"></textarea>
          <small class="block mt-1 text-xs text-n-slate-11">Texto que o lead ve pre-digitado ao abrir o WhatsApp pelo link.</small>
        </label>


        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">O que capturar</legend>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.gclid" class="mr-2"> gclid (Google Ads)</label>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.fbclid" class="mr-2"> fbclid (Meta)</label>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.ctwa_clid" class="mr-2"> ctwa_clid (Click-to-WhatsApp)</label>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.utms" class="mr-2"> UTMs</label>
        </fieldset>

        <!-- SiteUp Phase B: integracao Meta Lead Ads (recebe leads automaticamente) -->
        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">Meta Lead Ads (opcional)</legend>
          <label class="block mb-2">
            <span class="block text-xs font-medium text-n-slate-12 mb-1">ID da Pagina do Facebook</span>
            <input v-model="form.capture_config.meta_page_id" type="text" placeholder="Ex: 1029384756..."
              class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
            <small class="block mt-1 text-xs text-n-slate-11">Vincula este capturador a sua Pagina. Leads do formulario Meta entram automaticamente nesta inbox.</small>
          </label>
          <label class="block">
            <span class="block text-xs font-medium text-n-slate-12 mb-1">ID do formulario (pra importar os campos)</span>
            <div class="flex items-center gap-2">
              <input v-model="form.capture_config.meta_form_id" type="text" placeholder="Ex: 957348690536985"
                class="flex-1 px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
              <button type="button" :disabled="importing"
                class="px-3 py-2 rounded-md border border-n-brand text-n-brand text-sm whitespace-nowrap hover:bg-n-brand/10 disabled:opacity-50"
                @click="importMetaFields">
                {{ importing ? 'Importando...' : 'Importar campos' }}
              </button>
            </div>
            <small class="block mt-1 text-xs text-n-slate-11">Puxa as perguntas e opcoes do formulario pra voce escolher o que e qualificado abaixo.</small>
          </label>
        </fieldset>

        <!-- SiteUp Phase A: nota privada com as respostas do formulario -->
        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">Nota privada</legend>
          <label class="flex items-center gap-2 py-1 text-sm text-n-slate-12">
            <input type="checkbox" v-model="form.capture_config.private_note" />
            Postar nota privada com as respostas do formulario na conversa
          </label>
          <input v-if="form.capture_config.private_note" v-model="form.capture_config.private_note_intro"
            type="text" placeholder="📋 Respostas do formulario:"
            class="w-full mt-2 px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
          <small class="block mt-1 text-xs text-n-slate-11">Todas as respostas do formulario tambem viram campos personalizados da conversa automaticamente.</small>
        </fieldset>

        <!-- SiteUp Phase A: qualificacao por regras -->
        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">Qualificacao automatica</legend>

          <!-- SiteUp v309: qualificacao por opcao (a partir dos campos importados) -->
          <div v-if="optionFields.length" class="mb-3 space-y-2">
            <p class="text-xs text-n-slate-11">Marque as opcoes que voce considera <b>qualificadas</b>. Quem responder fora delas fica desqualificado.</p>
            <div v-for="f in optionFields" :key="f.key" class="border border-n-slate-3 rounded p-2">
              <div class="text-xs font-medium text-n-slate-12 mb-1">{{ f.label || f.key }}</div>
              <label v-for="opt in f.options" :key="opt" class="flex items-center gap-2 py-0.5 text-sm text-n-slate-12">
                <input type="checkbox" :checked="isOptionQualifying(f.key, opt)" @change="toggleOption(f.key, opt)" />
                {{ opt }}
              </label>
            </div>
          </div>

          <details class="mb-2">
            <summary class="text-xs text-n-slate-11 cursor-pointer select-none">Regras manuais (numerico, contem, igual...)</summary>
            <small class="block mt-2 mb-2 text-xs text-n-slate-11">Se TODAS as regras baterem, o lead recebe a label de qualificado; senao, desqualificado. Use a chave do campo do formulario (ex.: qual_e_a_distribuidora...).</small>
          <div v-for="(rule, i) in form.capture_config.qualification_rules" :key="i" class="flex items-center gap-1 mb-2">
            <input v-model="rule.field" type="text" placeholder="campo"
              class="w-32 px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            <select v-model="rule.op" class="px-1 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12">
              <option value="equals">=</option>
              <option value="not_equals">≠</option>
              <option value="contains">contem</option>
              <option value="gt">&gt;</option>
              <option value="gte">≥</option>
              <option value="lt">&lt;</option>
              <option value="lte">≤</option>
              <option value="in">esta em</option>
              <option value="not_in">nao esta em</option>
            </select>
            <textarea v-if="rule.op === 'in' || rule.op === 'not_in'" v-model="rule.value" rows="2" placeholder="uma opcao por linha"
              class="flex-1 px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12"></textarea>
            <input v-else v-model="rule.value" type="text" placeholder="valor"
              class="flex-1 px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            <button type="button" class="px-1 text-n-slate-11 hover:text-red-500" @click="form.capture_config.qualification_rules.splice(i, 1)">✕</button>
          </div>
          <button type="button" class="text-xs text-n-brand"
            @click="form.capture_config.qualification_rules.push({ field: '', op: 'equals', value: '' })">
            + Adicionar regra
          </button>
          </details>
          <div v-if="form.capture_config.qualification_rules.length" class="grid grid-cols-2 gap-2 mt-3">
            <label class="block">
              <span class="block text-xs text-n-slate-11 mb-1">Label qualificado</span>
              <input v-model="form.capture_config.qualified_label" type="text" placeholder="qualificado"
                class="w-full px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            </label>
            <label class="block">
              <span class="block text-xs text-n-slate-11 mb-1">Label desqualificado</span>
              <input v-model="form.capture_config.disqualified_label" type="text" placeholder="desqualificado"
                class="w-full px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            </label>
          </div>
          <label class="flex items-center gap-2 mt-3 pt-3 border-t border-n-slate-3 text-sm text-n-slate-12">
            <input type="checkbox" v-model="form.capture_config.skip_proactive_if_unqualified" />
            <span>Nao enviar mensagem proativa se o lead for desqualificado <small class="text-n-slate-11">(so registra o lead + nota privada)</small></span>
          </label>
        </fieldset>

        <div v-if="form.endpoint_type === 'webhook' && selectedInbox" class="bg-n-slate-2 rounded-md p-3 mb-4">
          <div v-if="inboxIsCloudApi" class="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 rounded">
            Inbox <b>Cloud API oficial</b> selecionada. Por restricao do WhatsApp, mensagem proativa nao pode ser enviada — quando o lead te enviar a 1a mensagem, os tracking IDs ja estarao salvos no contato.
          </div>
          <div v-else-if="supportsProactive" class="text-sm text-n-slate-12">
            <label class="flex items-center gap-2 mb-2">
              <input type="checkbox" v-model="form.send_proactive" />
              <span>Enviar mensagem proativa ao receber lead</span>
            </label>
            <div v-if="form.send_proactive" class="pl-6 mt-2 space-y-3">
              <label class="block">
                <span class="block text-xs font-medium text-n-slate-12 mb-1">Mensagem proativa</span>
                <textarea v-model="form.message_template" rows="3" placeholder="Olá {nome}! Recebemos seu contato. Como podemos ajudar?"
                  class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm"></textarea>
                <small class="block mt-1 text-xs text-n-slate-11">Suporta placeholders: {nome}, {first_name}, {email}, {telefone} + qualquer campo do formulario (ex.: {valor_conta}).</small>
              </label>
              <label class="block">
                <span class="block text-xs font-medium text-n-slate-12 mb-1">Imagem de boas-vindas (opcional)</span>
                <input type="file" accept="image/*" @change="onFile" class="text-xs" />
                <small v-if="endpoint?.welcome_image_url && !imageFile" class="block mt-1 text-xs text-n-slate-11">
                  Imagem atual: <a :href="endpoint.welcome_image_url" target="_blank" class="text-n-brand">ver</a>
                  <label class="ml-2"><input type="checkbox" v-model="removeImage" class="mr-1"> remover</label>
                </small>
              </label>
            </div>
          </div>
        </div>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">Dominios permitidos (opcional)</span>
          <input v-model="allowlistInput" type="text" placeholder="meusite.com.br, outrosite.com"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
          <small class="block mt-1 text-xs text-n-slate-11">Restringe POST do webhook a essas origens. Vazio = aceita qualquer.</small>
        </label>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">Limite de requisicoes/min por IP</span>
          <input v-model.number="form.rate_limit_per_minute" type="number" min="1" max="600"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
        </label>

        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input type="checkbox" v-model="form.active" />
          <span>Ativo</span>
        </label>
      </div>

      <footer class="px-6 py-4 border-t border-n-slate-4 flex justify-end gap-2">
        <button class="px-4 py-2 rounded-md border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3" @click="$emit('close')">Cancelar</button>
        <button class="px-4 py-2 rounded-md bg-n-brand text-white text-sm hover:bg-n-brand/90 disabled:opacity-50" :disabled="saving" @click="save">
          {{ saving ? 'Salvando...' : (isEdit ? 'Salvar' : 'Criar') }}
        </button>
      </footer>
    </div>
  </div>
</template>
